import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

const ANALYTICS_CONSENT_KEY = "pharmalify-cookie-consent-v1";
const ANALYTICS_CONSENT_EVENT = "pharmalify:consent";

const hasAnalyticsConsent = () => {
  try {
    return window.localStorage.getItem(ANALYTICS_CONSENT_KEY) === "accepted";
  } catch {
    return false;
  }
};

/**
 * Tracks page views by inserting a row into page_views on each route change.
 * Debounces to avoid duplicate inserts on rapid navigation.
 */
export function usePageViewTracker() {
  const location = useLocation();
  const lastTracked = useRef<string>("");
  const [consentVersion, setConsentVersion] = useState(() => (hasAnalyticsConsent() ? 1 : 0));

  useEffect(() => {
    const handleConsent = (event: Event) => {
      const detail = (event as CustomEvent<string>).detail;
      if (detail === "accepted") {
        setConsentVersion((current) => current + 1);
      }
    };

    window.addEventListener(ANALYTICS_CONSENT_EVENT, handleConsent);
    return () => window.removeEventListener(ANALYTICS_CONSENT_EVENT, handleConsent);
  }, []);

  useEffect(() => {
    if (!hasAnalyticsConsent()) return;

    const key = `${location.pathname}${location.search}`;
    if (key === lastTracked.current) return;
    lastTracked.current = key;

    // Fire GA4 page_view on SPA route change
    try {
      const gtag = (window as any).gtag;
      if (typeof gtag === "function") {
        gtag("event", "page_view", {
          page_path: location.pathname + location.search,
          page_location: window.location.href,
          page_title: document.title,
        });
      }
    } catch {
      // Silently fail — analytics should never block UX
    }

    const trackView = async () => {
      try {
        const { supabase } = await import("@/integrations/supabase/client");
        const { data: { user } } = await supabase.auth.getUser();
        await supabase.from("page_views" as any).insert({
          path: location.pathname,
          user_id: user?.id || null,
          user_agent: navigator.userAgent.slice(0, 512),
        });
      } catch {
        // Silently fail — analytics should never block UX
      }
    };

    // Defer tracking until the browser is idle so it stays out of the
    // critical request chain (improves LCP / network dependency tree).
    let idleHandle: number | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const ric = (window as any).requestIdleCallback as
      | ((cb: () => void, opts?: { timeout: number }) => number)
      | undefined;
    const cic = (window as any).cancelIdleCallback as
      | ((handle: number) => void)
      | undefined;

    if (typeof ric === "function") {
      idleHandle = ric(() => {
        timer = setTimeout(trackView, 1500);
      }, { timeout: 5000 });
    } else {
      timer = setTimeout(trackView, 3000);
    }

    return () => {
      if (timer) clearTimeout(timer);
      if (idleHandle !== undefined && typeof cic === "function") cic(idleHandle);
    };
  }, [location.pathname, location.search, consentVersion]);
}
